'use client';

import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
} from 'react';
import {
    type Locale,
    type Messages,
    messages,
    defaultLocale,
    getLocaleFromCookie,
    setCookie,
    COOKIE_NAME,
} from './index';

type NestedKeyOf<T> = T extends object
    ? {
            [K in keyof T]: K extends string
                ? T[K] extends object
                    ? `${K}` | `${K}.${NestedKeyOf<T[K]>}`
                    : `${K}`
                : never;
        }[keyof T]
    : never;

type TranslationKey = NestedKeyOf<Messages>;

interface I18nContextValue {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function getNestedValue(obj: unknown, path: string): string {
    const keys = path.split('.');
    let current: unknown = obj;

    for (const key of keys) {
        if (current === null || current === undefined) {
            return path;
        }
        current = (current as Record<string, unknown>)[key];
    }

    return typeof current === 'string' ? current : path;
}

function interpolate(template: string, params: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (_, key) => {
        return params[key] !== undefined ? String(params[key]) : `{${key}}`;
    });
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(defaultLocale);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setLocaleState(getLocaleFromCookie());
        setMounted(true);
    }, []);

    const setLocale = useCallback((newLocale: Locale) => {
        setLocaleState(newLocale);
        setCookie(COOKIE_NAME, newLocale);

        // Update HTML lang attribute
        if (typeof document !== 'undefined') {
            document.documentElement.lang = newLocale;
        }
    }, []);

    const t = useCallback(
        (key: TranslationKey, params?: Record<string, string | number>): string => {
            const translation = getNestedValue(messages[locale], key);
            if (params) {
                return interpolate(translation, params);
            }
            return translation;
        },
        [locale]
    );

    // During SSR and initial hydration, use default locale to avoid hydration mismatch
    const value: I18nContextValue = {
        locale: mounted ? locale : defaultLocale,
        setLocale,
        t,
    };

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
}

export function useTranslations(namespace?: string) {
    const { t, locale } = useI18n();

    const scopedT = useCallback(
        (key: string, params?: Record<string, string | number>): string => {
            const fullKey = namespace ? `${namespace}.${key}` : key;
            return t(fullKey as TranslationKey, params);
        },
        [t, namespace]
    );

    return { t: scopedT, locale };
}

export function useLocale(): Locale {
    const { locale } = useI18n();
    return locale;
}

export function useSetLocale(): (locale: Locale) => void {
    const { setLocale } = useI18n();
    return setLocale;
}

// Hook to get translated day names (index 0 = Sunday, 1 = Monday, etc.)
export function useDayNames(): { full: string[]; short: string[] } {
    const { t } = useTranslations('days');
    return {
        full: [
            t('sunday'),
            t('monday'),
            t('tuesday'),
            t('wednesday'),
            t('thursday'),
            t('friday'),
            t('saturday'),
        ],
        short: [
            t('sunShort'),
            t('monShort'),
            t('tueShort'),
            t('wedShort'),
            t('thuShort'),
            t('friShort'),
            t('satShort'),
        ],
    };
}
